import React, { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { AlertTriangle, Building2, CalendarClock, Link2, Save } from 'lucide-react'
import HotelChip from '@/components/groups/HotelChip'
import { cn } from '@/lib/utils'
import { formatRateSym } from '@/lib/calendar-utils'
import type { OverrideRangePreview, OverrideRangeRoom } from '@/types/calendar'
import type { PageIdentity } from '@/types/review'

interface BulkOverrideReviewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  range: OverrideRangePreview | null
  /** Span facts for the clear path, which produces no draft to preview. Plain
   *  date arithmetic — no rate is derived here. */
  fallbackSpan: { start: string; end: string; days: number; skippedPastDays: number }
  loading: boolean
  sym: string
  /** Today AT THE PROPERTY (YYYY-MM-DD) — the floor for the release date. */
  today: string
  saving: boolean
  /** True when the pending change removes prices rather than setting them. */
  clearing: boolean
  clearRestrictions: boolean
  onClearRestrictionsChange: (value: boolean) => void
  releaseOn: string
  onReleaseOnChange: (value: string) => void
  onConfirm: () => void
  /**
   * WHICH HOTEL AND WHICH BUSINESS these prices land on (change safety §5.10.4, R1.9): the page's
   * `pageIdentity`. Required, though it may be null, so the calendar cannot forget to pass it.
   */
  identity: PageIdentity | null | undefined
}

function longDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function dayNum(iso: string): string {
  return String(Number(iso.slice(8, 10)))
}

function weekday(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'narrow' })
}

function monthLabel(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short' })
}

function isWeekend(iso: string): boolean {
  const d = new Date(iso + 'T00:00:00').getDay()
  return d === 0 || d === 6
}

/**
 * A 28px-tall sparkline of one room's before/after shape.
 *
 * The matrix below carries the numbers; this carries the silhouette, which is
 * what makes the two modes distinguishable at a glance — a percentage traces the
 * computed contour, a fixed price flattens it to a bar. Pure geometry over
 * backend figures; no rate is derived here.
 */
function ShapeSpark({ room }: { room: OverrideRangeRoom }) {
  const geo = useMemo(() => {
    // "Before" is what each day SELLS AT now (live), not the engine's figure — the
    // grey line has to be the price the guest could see. Falls back to the engine
    // figure only for a day with no live row.
    const pts = room.series
      .map((d) => ({ current: d.live ?? d.current, after: d.after }))
      .filter((d) => d.current != null || d.after != null)
    if (pts.length < 2) return null
    const values = pts.flatMap((d) => [d.current, d.after].filter((v): v is number => v != null))
    const min = Math.min(...values)
    const span = Math.max(...values) - min || 1
    const x = (i: number) => (i * 100) / (pts.length - 1)
    const y = (v: number) => 26 - ((v - min) / span) * 22
    const path = (k: 'current' | 'after') =>
      pts
        .map((d, i) => (d[k] == null ? null : `${i === 0 ? 'M' : 'L'}${x(i)},${y(d[k] as number)}`))
        .filter(Boolean)
        .join(' ')
    return { before: path('current'), after: path('after') }
  }, [room.series])

  if (!geo) return null
  return (
    <svg viewBox="0 0 100 28" className="h-7 w-24 shrink-0" preserveAspectRatio="none" aria-hidden="true">
      <path d={geo.before} fill="none" stroke="#cbd5e1" strokeWidth={1.25} vectorEffect="non-scaling-stroke" />
      <path
        d={geo.after}
        fill="none"
        stroke="#0d9488"
        strokeWidth={1.75}
        vectorEffect="non-scaling-stroke"
        className="bulk-review-line"
        style={{ strokeDasharray: 400, strokeDashoffset: 400 }}
      />
    </svg>
  )
}

/** One count that is a reason to look twice. Rendered only when non-zero. */
function Flag({
  count,
  label,
  tone,
  hint,
}: {
  count: number
  label: string
  tone: 'neutral' | 'warn'
  hint: string
}) {
  if (count === 0) return null
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex cursor-default items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums',
            tone === 'neutral' ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-700'
          )}
        >
          {count} {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[260px] text-xs">
        {hint}
      </TooltipContent>
    </Tooltip>
  )
}

/** One matrix cell: what this room becomes on this date. */
function Cell({
  after,
  current,
  live,
  clampedTo,
  sym,
}: {
  after: number | null
  current: number | null
  live: number | null
  clampedTo: string | null
  sym: string
}) {
  // "was" is the live price; the engine figure rides along only when it differs,
  // because that is the number a percentage was applied to.
  const was = live ?? current
  const engineNote = live != null && current != null && live !== current ? ` (engine ${formatRateSym(current, sym)})` : ''
  if (after == null) {
    return <td className="px-1 py-1.5 text-center text-xs text-muted-foreground/30">—</td>
  }
  const up = was != null && after > was
  const down = was != null && after < was
  return (
    <td className="px-1 py-1.5 text-center">
      <span
        className={cn(
          'inline-block rounded px-1 text-xs font-semibold tabular-nums',
          clampedTo ? 'bg-amber-100 text-amber-800' : up ? 'text-emerald-700' : down ? 'text-rose-700' : 'text-foreground'
        )}
        title={
          clampedTo
            ? `Capped at the ${clampedTo}`
            : was != null
              ? `was ${formatRateSym(was, sym)}${engineNote}`
              : undefined
        }
      >
        {formatRateSym(after, sym)}
      </span>
    </td>
  )
}

export default function BulkOverrideReview({
  open,
  onOpenChange,
  range,
  fallbackSpan,
  loading,
  sym,
  today,
  saving,
  clearing,
  clearRestrictions,
  onClearRestrictionsChange,
  releaseOn,
  onReleaseOnChange,
  onConfirm,
  identity,
}: BulkOverrideReviewProps) {
  const totals = range?.totals
  // A clear has no drafted rooms, so it has no server range — its scope is the
  // selected span itself.
  const span = range ?? {
    start_date: fallbackSpan.start,
    end_date: fallbackSpan.end,
    days: fallbackSpan.days,
    skipped_past_days: fallbackSpan.skippedPastDays,
  }
  const dayCount = span.days

  // Every date in the span, taken from the first room's series so the matrix
  // columns and the row cells can never fall out of step.
  const dates = useMemo(() => range?.rooms[0]?.series.map((d) => d.date) ?? [], [range])

  // Month boundaries, so a span crossing months says so in the header.
  const monthSpans = useMemo(() => {
    const out: { label: string; count: number }[] = []
    for (const d of dates) {
      const label = monthLabel(d)
      const last = out[out.length - 1]
      if (last && last.label === label) last.count += 1
      else out.push({ label, count: 1 })
    }
    return out
  }, [dates])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1040px]">
        <TooltipProvider delayDuration={200}>
          <DialogHeader>
            <DialogTitle>{clearing ? 'Clear prices across these dates' : 'Review this change'}</DialogTitle>
            <DialogDescription>
              {longDate(span.start_date)} – {longDate(span.end_date)} ·{' '}
              <span className="tabular-nums">{dayCount}</span> {dayCount === 1 ? 'day' : 'days'}
              {totals ? (
                <>
                  {totals.writes > 0 && (
                    <>
                      {' '}
                      · <span className="tabular-nums">{totals.writes}</span> price
                      {totals.writes === 1 ? '' : 's'} written
                    </>
                  )}
                  {totals.removed > 0 && (
                    <>
                      {' '}
                      · <span className="tabular-nums">{totals.removed}</span> override
                      {totals.removed === 1 ? '' : 's'} removed
                    </>
                  )}
                </>
              ) : null}
            </DialogDescription>
            {/* The hotel first, the business beneath it, for everyone: the review is the moment the
                operator commits, so it says where the prices land before what they are. */}
            {identity && (
              <div className="space-y-1 pt-1" data-review-identity="">
                {/* Uncapped: the chip's row-annotation width would cut a long hotel name short here. */}
                <HotelChip name={identity.hotelName} tone="accent" className="max-w-full" />
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 [overflow-wrap:anywhere]">{identity.businessName}</span>
                </p>
              </div>
            )}
          </DialogHeader>

          <DialogBody className="min-w-0 overflow-x-hidden">
            {loading && !range && !clearing ? (
              <div className="space-y-3 py-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : (
              <div className="min-w-0 space-y-4">
                {span.skipped_past_days > 0 && (
                  <div className="flex items-start gap-2 rounded-xl border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      <span className="font-semibold tabular-nums text-foreground">{span.skipped_past_days}</span> past{' '}
                      {span.skipped_past_days === 1 ? 'day is' : 'days are'} skipped. Prices can only be changed from
                      today onward.
                    </span>
                  </div>
                )}

                {/* ── The matrix: rooms down, dates across ──────────────────── */}
                {/* The matrix wrapper must be width-constrained, or the table's
                    intrinsic width propagates up and scrolls the whole dialog. */}
                {!clearing && range && range.rooms.length > 0 && (
                  <div className="w-full min-w-0 overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
                    <div className="max-h-[46vh] w-full overflow-auto">
                      <table className="w-max min-w-full border-collapse text-xs">
                        <thead className="sticky top-0 z-20 bg-white">
                          <tr className="border-b border-border/40">
                            <th
                              rowSpan={2}
                              className="sticky left-0 z-30 min-w-[210px] border-r border-border/40 bg-white px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                            >
                              Room
                            </th>
                            {monthSpans.map((m, i) => (
                              <th
                                key={`${m.label}-${i}`}
                                colSpan={m.count}
                                className="border-b border-border/30 px-1 py-1 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                              >
                                {m.label}
                              </th>
                            ))}
                          </tr>
                          <tr className="border-b border-border/40">
                            {dates.map((d) => (
                              <th
                                key={d}
                                className={cn(
                                  'min-w-[46px] px-1 pb-1.5 text-center text-xs font-medium tabular-nums',
                                  isWeekend(d) ? 'text-foreground' : 'text-muted-foreground'
                                )}
                              >
                                <span className="block leading-none">{dayNum(d)}</span>
                                <span className="block text-xs leading-tight text-muted-foreground/60">
                                  {weekday(d)}
                                </span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {range.rooms.flatMap((room) => {
                            const rows = [
                              <tr key={room.room_type_id} className="border-b border-border/30">
                                <th
                                  scope="row"
                                  className="sticky left-0 z-10 border-r border-border/40 bg-white px-3 py-2 text-left align-middle font-normal"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-[13px] font-semibold text-foreground">{room.name}</div>
                                      <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                        <span
                                          className={cn(
                                            'rounded px-1 text-xs font-semibold tabular-nums',
                                            room.mode === 'relative'
                                              ? 'bg-primary/10 text-primary'
                                              : room.mode === 'remove'
                                                ? 'bg-rose-50 text-rose-700'
                                                : 'bg-slate-100 text-slate-700'
                                          )}
                                        >
                                          {room.mode === 'relative'
                                            ? `${room.adjust_value != null && room.adjust_value > 0 ? '+' : ''}${room.adjust_value}% each day`
                                            : room.mode === 'remove'
                                              ? `override removed · ${room.counts.removed} ${room.counts.removed === 1 ? 'day' : 'days'}`
                                              : `${formatRateSym(room.rate_amount ?? 0, sym)} every day`}
                                        </span>
                                        <Flag
                                          count={room.counts.replaced}
                                          label="replaced"
                                          tone="neutral"
                                          hint="Days that already had a price override. Saving overwrites them."
                                        />
                                        <Flag
                                          count={room.counts.mode_flips}
                                          label="mode change"
                                          tone="warn"
                                          hint="Days switching between a fixed price and a percentage. The previous instruction is discarded."
                                        />
                                        <Flag
                                          count={room.counts.clamped}
                                          label="capped"
                                          tone="warn"
                                          hint="Days where the result hit this room's floor or ceiling and was capped there."
                                        />
                                      </div>
                                    </div>
                                    <ShapeSpark room={room} />
                                  </div>
                                </th>
                                {room.series.map((d) => (
                                  <Cell key={d.date} after={d.after} current={d.current} live={d.live ?? null} clampedTo={d.clamped_to} sym={sym} />
                                ))}
                              </tr>,
                            ]

                            for (const child of room.children) {
                              const byDate = new Map(child.series.map((s) => [s.date, s.after]))
                              rows.push(
                                <tr key={`${room.room_type_id}-${child.room_type_id}`} className="border-b border-border/20 bg-slate-50/40">
                                  <th
                                    scope="row"
                                    className="sticky left-0 z-10 border-r border-border/40 bg-slate-50 px-3 py-1.5 text-left font-normal"
                                  >
                                    <div className="flex items-center gap-1.5 pl-3">
                                      <Link2 className="h-3 w-3 shrink-0 text-primary/60" />
                                      <span className="truncate text-[13px] text-muted-foreground">{child.name}</span>
                                      {child.offset_label != null && (
                                        <span className="shrink-0 rounded bg-primary/10 px-1 text-xs tabular-nums text-primary">
                                          {typeof child.offset_label === 'number'
                                            ? formatRateSym(child.offset_label, sym)
                                            : child.offset_label}
                                        </span>
                                      )}
                                    </div>
                                  </th>
                                  {room.series.map((d) => {
                                    const v = byDate.get(d.date)
                                    return (
                                      <td key={d.date} className="px-1 py-1.5 text-center">
                                        <span className="text-xs tabular-nums text-muted-foreground">
                                          {v != null ? formatRateSym(v, sym) : '—'}
                                        </span>
                                      </td>
                                    )
                                  })}
                                </tr>
                              )
                            }
                            // Combined inventory: each Same room sells at its twin's price, so it
                            // moves with the room (or follower) it copies. An empty series means it
                            // moves on a line the review did not compute: said, never a figure.
                            for (const same of room.same_rooms ?? []) {
                              const byDate = new Map(same.series.map((s) => [s.date, s.after]))
                              rows.push(
                                <tr key={`${room.room_type_id}-same-${same.room_type_id}`} className="border-b border-border/20 bg-primary/[0.02]">
                                  <th
                                    scope="row"
                                    className="sticky left-0 z-10 border-r border-border/40 bg-slate-50 px-3 py-1.5 text-left font-normal"
                                  >
                                    <div className="flex items-center gap-1.5 pl-3">
                                      <Link2 className="h-3 w-3 shrink-0 text-primary/60" />
                                      <span className="truncate text-[13px] text-muted-foreground">{same.name}</span>
                                      <span className="shrink-0 rounded bg-primary/10 px-1 text-xs text-primary">{same.label}</span>
                                    </div>
                                  </th>
                                  {same.series.length === 0 ? (
                                    <td colSpan={room.series.length} className="px-2 py-1.5 text-center text-xs text-muted-foreground">
                                      Moves with the room it copies
                                    </td>
                                  ) : (
                                    room.series.map((d) => {
                                      const v = byDate.get(d.date)
                                      return (
                                        <td key={d.date} className="px-1 py-1.5 text-center">
                                          <span className="text-xs tabular-nums text-muted-foreground">
                                            {v != null ? formatRateSym(v, sym) : 'No change'}
                                          </span>
                                        </td>
                                      )
                                    })
                                  )}
                                </tr>
                              )
                            }
                            return rows
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!clearing &&
                  range?.rooms.some((r) => r.mode === 'fixed' && r.counts.writes > 1) && (
                    <p className="flex items-start gap-1.5 text-xs text-amber-700">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      A fixed price sets every day to the same number, flattening the day-to-day shape. Use a percentage
                      to keep it.
                    </p>
                  )}

                {clearing && (
                  <div className="rounded-xl border border-border/60 bg-white p-4 shadow-sm">
                    <p className="text-sm text-foreground">
                      Price overrides will be removed across{' '}
                      <span className="font-semibold tabular-nums">{dayCount}</span>{' '}
                      {dayCount === 1 ? 'day' : 'days'}. Those dates go back to the computed rate.
                    </p>
                    <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2">
                      <div>
                        <Label htmlFor="clear-restrictions" className="cursor-pointer text-xs font-medium">
                          Also clear restrictions
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Off by default: min stay, stop sell and closed-to rules are kept.
                        </p>
                      </div>
                      <Switch
                        id="clear-restrictions"
                        checked={clearRestrictions}
                        onCheckedChange={onClearRestrictionsChange}
                      />
                    </div>
                  </div>
                )}

                {!clearing && (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-muted/30 px-3 py-2.5">
                    <div>
                      <Label htmlFor="release-on" className="text-xs font-medium">
                        Release on
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Optional. On this date these prices go back to computed.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {releaseOn && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 rounded-lg px-2 text-xs text-muted-foreground"
                          onClick={() => onReleaseOnChange('')}
                        >
                          Never
                        </Button>
                      )}
                      <input
                        id="release-on"
                        type="date"
                        value={releaseOn}
                        min={today}
                        onChange={(e) => onReleaseOnChange(e.target.value)}
                        className="h-9 rounded-lg border border-border bg-white px-2 text-xs tabular-nums shadow-sm focus:border-primary focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {totals && !clearing && (totals.same_rooms_following ?? 0) > 0 && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-semibold tabular-nums text-foreground">{totals.same_rooms_following}</span>{' '}
                    {totals.same_rooms_following === 1
                      ? 'Same room moves with the room it copies'
                      : 'Same rooms move with the rooms they copy'}
                  </p>
                )}

                {totals && !clearing && (totals.children_following > 0 || totals.children_detached > 0) && (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    {totals.children_following > 0 && (
                      <span>
                        <span className="font-semibold tabular-nums text-foreground">{totals.children_following}</span>{' '}
                        linked {totals.children_following === 1 ? 'room moves' : 'rooms move'} with their lead, with no
                        separate override
                      </span>
                    )}
                    {totals.children_detached > 0 && (
                      <span>
                        · <span className="font-semibold tabular-nums text-foreground">{totals.children_detached}</span>{' '}
                        linked {totals.children_detached === 1 ? 'room keeps' : 'rooms keep'} its own price
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
              Back
            </Button>
            <Button
              type="button"
              className={cn('rounded-lg', clearing && 'bg-rose-600 hover:bg-rose-700')}
              onClick={onConfirm}
              disabled={saving || (loading && !range)}
              aria-busy={saving || undefined}
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {clearing
                ? `Clear ${dayCount} ${dayCount === 1 ? 'day' : 'days'}`
                : `Apply to ${dayCount} ${dayCount === 1 ? 'day' : 'days'}`}
            </Button>
          </DialogFooter>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  )
}
